import { createContext, useContext, useEffect, useState } from "react";

const translations = {
  en: {
    appName: "Travel CRM",
    tagline: "Manage clients, build programs, generate booking documents.",
    // Auth
    signIn: "Sign in",
    email: "Email",
    password: "Password",
    loginWelcome: "Welcome back",
    loginSubtitle: "Enter your credentials to access your dashboard",
    invalidCredentials: "Invalid email or password",
    logout: "Sign out",
    // Nav
    dashboard: "Dashboard",
    clients: "Clients",
    programs: "Programs",
    bookings: "Bookings",
    users: "Team",
    suppliers: "Suppliers",
    controlPanel: "Control panel",
    settings: "Settings",
    // Dashboard
    totalClients: "Total clients",
    totalPrograms: "Programs",
    totalBookings: "Bookings",
    totalRevenue: "Est. revenue",
    recentBookings: "Recent bookings",
    // Clients
    newClient: "New client",
    addClient: "Add client",
    editClient: "Edit client",
    name: "Name",
    phone: "Phone",
    nationality: "Nationality",
    passport: "Passport No.",
    travelers: "Travelers",
    notes: "Notes",
    save: "Save",
    cancel: "Cancel",
    actions: "Actions",
    edit: "Edit",
    delete: "Delete",
    search: "Search",
    // Programs
    newProgram: "New program",
    addProgram: "Add program",
    editProgram: "Edit program",
    programName: "Program name",
    programType: "Program type",
    fullPackage: "Full package",
    flightHotel: "Flights + hotels",
    flightOnly: "Flights only",
    custom: "Custom program",
    description: "Description",
    durationDays: "Duration (days)",
    currency: "Currency",
    flights: "Flights",
    hotels: "Hotels",
    visas: "Visas",
    tours: "Tours",
    tourName: "Tour name",
    tourDuration: "Duration (e.g. 3h)",
    itinerary: "Itinerary",
    pricing: "Pricing",
    airline: "Airline",
    flightNo: "Flight No.",
    departureAirport: "Departure airport",
    departureTime: "Departure time",
    arrivalAirport: "Arrival airport",
    arrivalTime: "Arrival time",
    checkInTime: "Check-in time",
    checkOutTime: "Check-out time",
    confirmationNo: "Confirmation no.",
    from: "From",
    to: "To",
    date: "Date",
    city: "City",
    checkIn: "Check-in",
    checkOut: "Check-out",
    roomType: "Room type",
    nights: "Nights",
    country: "Country",
    visaType: "Visa type",
    fee: "Fee",
    day: "Day",
    item: "Item",
    amount: "Amount",
    addRow: "Add row",
    remove: "Remove",
    // Bookings
    newBooking: "New booking",
    addBooking: "Create booking",
    client: "Client",
    program: "Program",
    startDate: "Start date",
    endDate: "End date",
    refNo: "Ref No.",
    status: "Status",
    createdAt: "Created",
    downloadPdf: "Download PDF",
    // Settings
    companyName: "Company name",
    website: "Website",
    address: "Address",
    primaryColor: "Primary color",
    accentColor: "Accent color",
    logoUrl: "Logo URL",
    uploadLogo: "Upload logo",
    logoUploaded: "Logo uploaded",
    crNumber: "Commercial Reg. No.",
    licenseNumber: "Tourism License No.",
    licenseCategory: "License Category",
    saved: "Saved",
    // Payment
    totalAmount: "Total amount",
    deposit: "Deposit paid",
    balanceDue: "Balance due",
    paymentStatus: "Payment status",
    paidFull: "Paid in full",
    partial: "Partial",
    unpaid: "Unpaid",
    // Share
    shareWhatsapp: "Send on WhatsApp",
    editBooking: "Edit booking",
    createBooking: "Create booking",
    stepClient: "Select client",
    stepService: "Select service",
    stepSupplier: "Select supplier",
    stepDetails: "Details & payment",
    searchClient: "Search clients…",
    searchProgram: "Search programs…",
    searchSupplier: "Search suppliers…",
    skipStep: "Skip",
    next: "Next",
    back: "Back",
    // Users
    addUser: "Add member",
    role: "Role",
    admin: "Admin",
    sales: "Sales",
    // Suppliers
    newSupplier: "New supplier",
    addSupplier: "Add supplier",
    editSupplier: "Edit supplier",
    supplierName: "Supplier name",
    supplierType: "Type",
    contactPerson: "Contact person",
    typeAirline: "Airline",
    typeHotel: "Hotel",
    typeVisa: "Visa services",
    typeTransport: "Transport",
    typeGroundService: "Ground services",
    typeOther: "Other",
    // Payment methods
    paymentMethods: "Payment methods",
    paymentMethod: "Payment method",
    newPaymentMethod: "New method",
    addPaymentMethod: "Add payment method",
    editPaymentMethod: "Edit payment method",
    active: "Active",
    inactive: "Inactive",
    // Reports
    salesReport: "Sales report",
    reports: "Reports",
    apply: "Apply",
    collected: "Collected",
    outstanding: "Outstanding",
    bookingsDetail: "Bookings detail",
    accountant: "Accountant",
    taxNumber: "VAT / Tax number",
    companyInfo: "Company info",
    manageCompanyInfo: "Company details",
    // Reminders
    paymentReminders: "Payment reminders",
    scanNow: "Scan now",
    pending: "Pending",
    sentLabel: "Sent",
    all: "All",
    filter: "Filter",
    timing: "Timing",
    dueToday: "Due today",
    dueInDays: "In {n} days",
    overdueDays: "Overdue {n} days",
    emptyReminders: "No reminders — all clients are up to date.",
    // Control Panel
    totalSuppliers: "Suppliers",
    totalTeam: "Team members",
    manageTeam: "Manage team",
    manageSuppliers: "Manage suppliers",
    manageSettings: "Company settings",
    quickActions: "Quick actions",
    platformStats: "Platform overview",
    // Misc
    yes: "Yes",
    no: "No",
    confirm: "Confirm",
    confirmDelete: "Delete this record?",
    loading: "Loading...",
    empty: "No records yet.",
  },
  ar: {
    appName: "منصة السياحة",
    tagline: "أدر عملاءك، اصنع البرامج السياحية، وأنشئ وثائق الحجز بضغطة.",
    signIn: "تسجيل الدخول",
    email: "البريد الإلكتروني",
    password: "كلمة المرور",
    loginWelcome: "مرحباً بعودتك",
    loginSubtitle: "أدخل بياناتك للوصول إلى لوحة التحكم",
    invalidCredentials: "بيانات الدخول غير صحيحة",
    logout: "تسجيل الخروج",
    dashboard: "الرئيسية",
    clients: "العملاء",
    programs: "البرامج",
    bookings: "الحجوزات",
    users: "الفريق",
    suppliers: "الموردين",
    controlPanel: "لوحة التحكم",
    settings: "الإعدادات",
    totalClients: "إجمالي العملاء",
    totalPrograms: "البرامج",
    totalBookings: "الحجوزات",
    totalRevenue: "الإيرادات التقديرية",
    recentBookings: "أحدث الحجوزات",
    newClient: "عميل جديد",
    addClient: "إضافة عميل",
    editClient: "تعديل العميل",
    name: "الاسم",
    phone: "الجوال",
    nationality: "الجنسية",
    passport: "رقم الجواز",
    travelers: "عدد المسافرين",
    notes: "ملاحظات",
    save: "حفظ",
    cancel: "إلغاء",
    actions: "إجراءات",
    edit: "تعديل",
    delete: "حذف",
    search: "بحث",
    newProgram: "برنامج جديد",
    addProgram: "إضافة برنامج",
    editProgram: "تعديل البرنامج",
    programName: "اسم البرنامج",
    programType: "نوع البرنامج",
    fullPackage: "حزمة شاملة",
    flightHotel: "طيران وفنادق",
    flightOnly: "طيران فقط",
    custom: "برنامج مخصص",
    description: "الوصف",
    durationDays: "المدة (أيام)",
    currency: "العملة",
    flights: "الرحلات الجوية",
    hotels: "الفنادق",
    visas: "التأشيرات",
    tours: "الجولات السياحية",
    tourName: "اسم الجولة",
    tourDuration: "المدة (مثال: 3 ساعات)",
    itinerary: "البرنامج اليومي",
    pricing: "الأسعار",
    airline: "شركة الطيران",
    flightNo: "رقم الرحلة",
    departureAirport: "مطار الإقلاع",
    departureTime: "وقت الإقلاع",
    arrivalAirport: "مطار الوصول",
    arrivalTime: "وقت الوصول",
    checkInTime: "وقت الدخول",
    checkOutTime: "وقت المغادرة",
    confirmationNo: "رقم التأكيد",
    from: "من",
    to: "إلى",
    date: "التاريخ",
    city: "المدينة",
    checkIn: "الدخول",
    checkOut: "الخروج",
    roomType: "نوع الغرفة",
    nights: "الليالي",
    country: "الدولة",
    visaType: "نوع التأشيرة",
    fee: "الرسوم",
    day: "اليوم",
    item: "البند",
    amount: "المبلغ",
    addRow: "إضافة سطر",
    remove: "حذف",
    newBooking: "حجز جديد",
    addBooking: "إنشاء حجز",
    client: "العميل",
    program: "البرنامج",
    startDate: "تاريخ البداية",
    endDate: "تاريخ النهاية",
    refNo: "الرقم المرجعي",
    status: "الحالة",
    createdAt: "تاريخ الإنشاء",
    downloadPdf: "تحميل PDF",
    companyName: "اسم الشركة",
    website: "الموقع الإلكتروني",
    address: "العنوان",
    primaryColor: "اللون الأساسي",
    accentColor: "اللون الثانوي",
    logoUrl: "رابط الشعار",
    uploadLogo: "رفع الشعار",
    logoUploaded: "تم رفع الشعار",
    crNumber: "السجل التجاري",
    licenseNumber: "ترخيص السياحة",
    licenseCategory: "فئة الترخيص",
    saved: "تم الحفظ",
    totalAmount: "الإجمالي",
    deposit: "المدفوع",
    balanceDue: "المتبقي",
    paymentStatus: "حالة الدفع",
    paidFull: "مدفوع بالكامل",
    partial: "دفعة جزئية",
    unpaid: "غير مدفوع",
    shareWhatsapp: "إرسال عبر واتساب",
    editBooking: "تعديل الحجز",
    createBooking: "إنشاء الحجز",
    stepClient: "اختيار العميل",
    stepService: "اختيار الخدمة",
    stepSupplier: "اختيار المورد",
    stepDetails: "التفاصيل والدفع",
    searchClient: "ابحث عن عميل…",
    searchProgram: "ابحث عن خدمة…",
    searchSupplier: "ابحث عن مورد…",
    skipStep: "تخطّي",
    next: "التالي",
    back: "السابق",
    addUser: "إضافة عضو",
    role: "الدور",
    admin: "مدير",
    sales: "مبيعات",
    newSupplier: "مورد جديد",
    addSupplier: "إضافة مورد",
    editSupplier: "تعديل المورد",
    supplierName: "اسم المورد",
    supplierType: "النوع",
    contactPerson: "الشخص المسؤول",
    typeAirline: "شركة طيران",
    typeHotel: "فندق",
    typeVisa: "خدمات تأشيرات",
    typeTransport: "نقل ومواصلات",
    typeGroundService: "خدمات أرضية",
    typeOther: "أخرى",
    paymentMethods: "طرق الدفع",
    paymentMethod: "طريقة الدفع",
    newPaymentMethod: "طريقة جديدة",
    addPaymentMethod: "إضافة طريقة دفع",
    editPaymentMethod: "تعديل طريقة الدفع",
    active: "مفعّل",
    inactive: "معطّل",
    salesReport: "تقرير المبيعات",
    reports: "التقارير",
    apply: "تطبيق",
    collected: "المحصّل",
    outstanding: "المتبقي",
    bookingsDetail: "تفاصيل الحجوزات",
    accountant: "محاسب",
    taxNumber: "الرقم الضريبي",
    companyInfo: "بيانات الشركة",
    manageCompanyInfo: "بيانات الشركة",
    paymentReminders: "تذكيرات الدفع",
    scanNow: "فحص الآن",
    pending: "معلّق",
    sentLabel: "تم الإرسال",
    all: "الكل",
    filter: "التصفية",
    timing: "التوقيت",
    dueToday: "اليوم",
    dueInDays: "بعد {n} أيام",
    overdueDays: "متأخر {n} أيام",
    emptyReminders: "لا توجد تذكيرات — كل العملاء منتظمون بالدفع.",
    totalSuppliers: "الموردين",
    totalTeam: "أعضاء الفريق",
    manageTeam: "إدارة الفريق",
    manageSuppliers: "إدارة الموردين",
    manageSettings: "إعدادات الشركة",
    quickActions: "إجراءات سريعة",
    platformStats: "نظرة عامة على المنصة",
    yes: "نعم",
    no: "لا",
    confirm: "تأكيد",
    confirmDelete: "هل تريد حذف هذا السجل؟",
    loading: "جاري التحميل...",
    empty: "لا توجد بيانات بعد.",
  },
};

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem("lang") || "ar");

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.body.classList.toggle("font-ar", lang === "ar");
    document.body.classList.toggle("font-en", lang !== "ar");
    localStorage.setItem("lang", lang);
  }, [lang]);

  const setLang = (l) => setLangState(l);
  const t = (key) => translations[lang]?.[key] ?? translations.en[key] ?? key;

  return (
    <I18nContext.Provider value={{ lang, setLang, t, dir: lang === "ar" ? "rtl" : "ltr" }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be inside I18nProvider");
  return ctx;
};
